// Need to use the React-specific entry point to import createApi
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { PublicClientApplication } from "@azure/msal-browser";
import { loginRequest, msalConfig } from './../authConfig';
import { IDeleteUser, IUpdateUser, IUser } from '../interfaces/IUser'
import IMeterTreeNode from '../interfaces/IMeterTreeNode'
import { IEditOrganisation, INewOrganisation, IOrganisationTreeNode } from '../interfaces/IOrganisationTreeNode'
import { IMeterDetails, INewMeterDetails } from '../interfaces/IMeterDetails';
import { IMeterGroupDetails, INewMeterGroupDetails, IUpdateMeterGroupDetails } from '../interfaces/IMeterGroupDetails';
import { IMeterSiteDetails, INewMeterSiteDetails } from '../interfaces/IMeterSiteDetails';
import { IDataSource, IDataSourceQuery } from '../interfaces/IDataSource'
import IMeterTreeNodeCategory from '../interfaces/IMeterTreeNodeCategory';
import IMeterTreeNodeDeBs from '../interfaces/IMeterTreeNodeDeBs';
import IExpectedData from '../interfaces/IExpectedData';
import { IDataType } from '../interfaces/IDataType';
import IDataUnit from '../interfaces/IDataUnit';
import IHardware from '../interfaces/IHardware';
import { IMeterHardware, IMeterHardwareQuery, IMoveMeterHardware } from '../interfaces/IMeterHardware';
import IMeterDataChannel, { IMeterDataChannelUpdate } from '../interfaces/IMeterDataChannel';
import IMeterDataChannelMultiplier, { IAddMeterDataChannelMultiplier } from '../interfaces/IMeterDataChannelMultiplier';
import IDashboardQueryDTO from '../interfaces/IDashboard_VolumeQueryDTO';
import { IDataChannelCopyDTO, IDataChannelCloneDTO } from '../interfaces/IDataChannelCopyDTO';
import { IAlarmsQuery } from '../interfaces/IAlarmsQuery';
import { IChartTab, IChartTabAddNode, IChartTabUpdate } from '../interfaces/IChartTab';
import IChartDataQuery from '../interfaces/IChartDataQuery';
import { IAlarmFilterDTO, IAlarmFilterListDTO } from '../interfaces/IAlarmFilterListDTO';
import IAPIKey from '../interfaces/IAPIKey';
import { IInputData } from '../interfaces/IInputData';
import { IDataSourceDataChannelData } from '../interfaces/IDataSourceDataChannelData';
import { IDataSourceMeterData } from '../interfaces/IDataSourceMeterData';
import { IDataSourceType } from '../interfaces/IDataSourceType';
import { IMissingData } from '../interfaces/IMissingData';
import { IDataRequest } from '../interfaces/IDataRequest';
import { IDataRequestHistoryQuery, IDataRequestHistoryStatusQuery } from '../interfaces/IDataRequestHistoryQuery';
import { IAutomaticRequest } from '../interfaces/IAutomaticRequest';
import { IUpdateAutomaticRequest } from '../interfaces/IUpdateAutomaticRequest';
import { IAddDataCorrection, IDataCorrection, IDataCorrectionQuery } from '../interfaces/IDataCorrection';
import { IMeterOrganisation, IMeterUpdateControllingOrganisation } from './../interfaces/IMeterOrganisation';
import { ISecurityRoles } from './../interfaces/ISecurityRoles';
import { IAlarmSetting, INewAlarmSetting, IUpdateAlarmSetting } from './../interfaces/IAlarmSetting';
import { IDeleteMeterTreeNodeCollectionNode, IMeterTreeNodeCollection, INewMeterTreeNodeCollectionNode } from './../interfaces/IMeterTreeNodeCollection';
import { IDeBsReport, IDeBsReportQuery } from './../interfaces/IDeBsReport';
import { IDeBsDrakewellFile } from './../interfaces/IDeBsDrakewellFile';
import { ISecurityTree } from '../interfaces/ISecurityTree';
import { INewUser } from '../interfaces/INewUser';
import { IDeleteSecurityGroup, INewSecurityGroup, ISecurityGroupPermissions, IUpdateSecurityGroup } from '../interfaces/IUpdateSecurityGroup';
import { IOrganisationSecurity } from '../interfaces/IOrganisationSecurity';
import { IUpdateUserSetting, IUserSetting } from '../interfaces/IUserSetting';
import { ILicense, ILicensedHardware, IUpdateLicenseAutoRenewal, IAddHardwareLicense, ICreateLicense } from '../interfaces/ILicense'; 
import { IAlarmStatus, IUpdateAlarmStatus } from '../interfaces/IAlarmStatus';
import { IAlarmResponse, IDataAlarm } from '../interfaces/IAlarm';
import IDataAPIs from '../interfaces/IDataAPIs';


const msalInstance = new PublicClientApplication(msalConfig);

async function acquireAccessToken(msalInstance: any) {
    const activeAccount = msalInstance.getActiveAccount(); // This will only return a non-null value if you have logic somewhere else that calls the setActiveAccount API
    const accounts = msalInstance.getAllAccounts();

    if (!activeAccount && accounts.length === 0) {
        /*
        * User is not signed in. Throw error or wait for user to login.
        * Do not attempt to log a user in outside of the context of MsalProvider
        */
        // msalInstance.loginRedirect(loginRequest);
        return;
    }

    // Refresh token only valid for 24 hours, need to go through login flow from scratch
    if (accounts.length && accounts[0].idTokenClaims?.exp < (new Date().getTime() - (60 * 60 * 24 * 1000)) / 1000) {
        msalInstance.logout({ account: accounts[0] });
        return;
    }

    const request = {
        ...loginRequest,
        account: activeAccount || accounts[0]
    };

    return await msalInstance.acquireTokenSilent(request).then((authResult: any) => {
        return authResult.accessToken;
    }, (error: any) => {
        if (error.message.indexOf('AADB2C90077') > -1) {
            msalInstance.loginRedirect(loginRequest);
            return;
        };
        console.error(error);
    });
};

// let token: string;
// acquireAccessToken(msalInstance).then((accessTokenResponse) => {
//        // Acquire token silent success
//        token = accessTokenResponse.accessToken;
//      })

let BaseUrl = "https://api.metervue.net/";

if (window.location.host.indexOf('-demo') > -1) {
    BaseUrl = "https://mvuk-api-demo.azurewebsites.net/";
}
if (window.location.host.indexOf('localhost') > -1) {
    BaseUrl = "https://localhost:44348/";
}

const allTagTypes = ['User',
    'MeterTree',
    'MeterTreeNodeCategory',
    'Meter',
    'MeterGroup',
    'MeterSite',
    'MeterTreeNodeDeBs',
    'MeterExpectedData',
    'MeterHardware',
    'MeterDataChannels',
    'Alarms',
    'AlarmFilters',
    'ChartTabs',
    'ChartData',
    'DataSources',
    'DataSource',
    'MissingData',
    'DataRequestHistory',
    'AutomaticRequests',
    'DataCorrections',
    'MeterOrganisations',
    'Dashboard',
    'SecurityRoles',
    'AlarmSettings',
    'MeterTreeNodeCollections',
    'MeterTreeNodeCollection',
    'DeBsReports',
    'ChartSettings',
    'Security',
    'Organisation',
    'OrganisationSecurity',
    'SecurityGroup',
    'SecurityGroupPermissions',
    'UserSettings',
    'License',
    'LicenseHardware',
    'AlarmStatus',
    'UserDetails',
];

// Define a service using a base URL and expected endpoints
export const metervueApi = createApi({

    reducerPath: 'metervueApi',
    baseQuery: fetchBaseQuery({
        baseUrl: BaseUrl,
        prepareHeaders: async (headers, { getState }) => {
            var token = await acquireAccessToken(msalInstance);
            headers.set('authorization', `Bearer ${token}`)
            return headers
        },
    }),
    tagTypes: allTagTypes,
    endpoints: (builder) => ({
        MeterTree: builder.query<IMeterTreeNode[], string>({
            query: () => "MeterTree",
            providesTags: ['MeterTree']
        }),
        MeterTree_Meter: builder.query<IMeterDetails, number>({
            query: NodeID => `MeterTree/Meter/${NodeID}`,
            providesTags: (result, error, arg) => [{ type: 'Meter', id: arg }]
        }),
        DeleteMeter: builder.mutation({
            query: (nodeID) => `MeterTree/Meter/Delete/${nodeID}`,
            invalidatesTags: (result, error, arg) => ["MeterTree", { type: 'Meter', id: arg.nodeID }]
        }),
        MeterTree_Group: builder.query<IMeterGroupDetails, number>({
            query: NodeID => `MeterTree/Group/${NodeID}`,
            providesTags: (result, error, arg) => [{ type: 'MeterGroup', id: arg }]
        }),
        MeterTree_Site: builder.query<IMeterSiteDetails, number>({
            query: NodeID => `MeterTree/Site/${NodeID}`,
            providesTags: (result, error, arg) => [{ type: 'MeterSite', id: arg }]
        }),
        MeterTree_AddMeter: builder.mutation({
            query(body: INewMeterDetails) {
                return {
                    url: `MeterTree/Meter/Add`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: ['MeterTree'],
        }),
        MeterTree_UpdateMeter: builder.mutation({
            query(body: IMeterDetails) {
                return {
                    url: `MeterTree/Meter/Update`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => ["MeterTree", { type: 'Meter', id: arg.nodeID }]
        }),
        MeterGrantAccess: builder.mutation({
            query: ({ meterID, organisationID }) => `MeterTree/Meter/GrantAccess?MeterID=${meterID}&OrganisationID=${organisationID}`,
            invalidatesTags: (result, error, arg) => [{ type: 'MeterOrganisations', id: arg.meterID }]
        }),
        MeterRevokeAccess: builder.mutation({
            query: ({ meterID, organisationID }) => `MeterTree/Meter/RevokeAccess?MeterID=${meterID}&OrganisationID=${organisationID}`,
            invalidatesTags: (result, error, arg) => [{ type: 'MeterOrganisations', id: arg.meterID }]
        }),
        MeterTree_AddGroup: builder.mutation({
            query(body: INewMeterGroupDetails) {
                return {
                    url: `MeterTree/Group/Add`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: ['MeterTree'],
        }),
        MeterTree_UpdateGroup: builder.mutation({
            query(body: IUpdateMeterGroupDetails) {
                return {
                    url: `MeterTree/Group/Update`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => ["MeterTree", { type: 'MeterGroup', id: arg.nodeID }]
        }),
        MeterTree_AddSite: builder.mutation({
            query(body: INewMeterSiteDetails) {
                return {
                    url: `MeterTree/Site/Add`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: ['MeterTree'],
        }),
        MeterTree_UpdateMeterSecurity: builder.mutation({
            query(body: IMeterOrganisation) {
                return {
                    url: `MeterTree/Meter/UpdateSecurity`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => ["MeterOrganisations", { type: 'Meter', id: arg.nodeID }]
        }),
        MeterTree_UpdateMeterControllingOrganisation: builder.mutation({
            query(body: IMeterUpdateControllingOrganisation) {
                return {
                    url: `MeterTree/Meter/UpdateControllingOrganisation`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => ["MeterTree", "MeterOrganisations", { type: 'Meter', id: arg.nodeID }]
        }),
        MeterTree_UpdateSite: builder.mutation({
            query(body: IMeterSiteDetails) {
                return {
                    url: `MeterTree/Site/Update`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => ["MeterTree", { type: 'MeterSite', id: arg.nodeID }]
        }),
        DeleteMeterSite: builder.mutation({
            query: (nodeID) => `MeterTree/Site/Delete/${nodeID}`,
            invalidatesTags: (result, error, arg) => ["MeterTree", { type: 'MeterSite', id: arg.nodeID }]
        }),
        DataSources: builder.query<any, IDataSourceQuery>({
            query: (body: IDataSourceQuery) => {
                return {
                    url: 'DataSources',
                    method: 'POST',
                    body,
                };
            },
            keepUnusedDataFor: 20,
        }),
        DataSource: builder.query<IDataSource, string>({
            query: DataSourceGUID => `DataSources/${DataSourceGUID}`,
            providesTags: (result, error, arg) => [{ type: 'DataSource', id: arg }]
        }),
        DataSourceInterpretedContent: builder.query<IInputData, string>({
            query: DataSourceGUID => `DataSources/${DataSourceGUID}/InterpretedContent`
        }),
        DataSourceDataChannelData: builder.query<IDataSourceDataChannelData[], string>({
            query: DataSourceGUID => `DataSources/${DataSourceGUID}/ChannelData`
        }),
        DataSourceMeterData: builder.query<IDataSourceMeterData[], string>({
            query: DataSourceGUID => `DataSources/${DataSourceGUID}/MeterData`
        }),
        DataSourceUpdateHardware: builder.mutation({
            query: ({ DataSourceGUID, HardwareGUID }) => `DataSources/UpdateHardware/${DataSourceGUID}/${HardwareGUID}`,
            invalidatesTags: (result, error, arg) => ['DataSources', { type: 'DataSource', id: arg.DataSourceGUID }]
        }),
        DataSourceUpdateDataSourceType: builder.mutation({
            query: ({ DataSourceGUID, DataSourceTypeID }) => `DataSources/UpdateDataSourceType/${DataSourceGUID}/${DataSourceTypeID}`,
            invalidatesTags: (result, error, arg) => ['DataSources', { type: 'DataSource', id: arg.DataSourceGUID }]
        }),
        DataSourceReprocess: builder.mutation({
            query: DataSourceGUID => `DataSources/${DataSourceGUID}/Reprocess`,
            invalidatesTags: (result, error, arg) => ['DataSources', { type: 'DataSource', id: arg.DataSourceGUID }]
        }),
        DataSourceInvalidate: builder.mutation({
            query: DataSourceGUID => `DataSources/${DataSourceGUID}/Invalidate`,
            invalidatesTags: (result, error, arg) => ['DataSources', { type: 'DataSource', id: arg.DataSourceGUID }]
        }),
        DataSourceTypes: builder.query<IDataSourceType[], string>({
            query: () => `DataSources/Types`
        }),
        Alarms: builder.query<any, IAlarmsQuery>({
            query: (body: IAlarmsQuery) => {
                return {
                    url: 'Alarms/List',
                    method: 'POST',
                    body,
                };
            },
            keepUnusedDataFor: 20,
            providesTags: (result, error, arg) => ['Alarms']
        }),
        Alarm: builder.query<any, number>({
            query: AlarmID => `Alarms/${AlarmID}`,
            providesTags: (result, error, arg) => [{ type: 'Alarms', id: arg }]
        }),
        DataAlarms: builder.query<IDataAlarm[], number>({
            query: AlarmID => `Alarms/DataAlarms/${AlarmID}`
        }),
        AlarmStatusHistory: builder.query<IAlarmStatus[], number>({
            query: AlarmID => `Alarms/Status/History/${AlarmID}`,
            providesTags: (result, error, arg) => [{ type: 'AlarmStatus', id: arg }]
        }),
        AlarmStatusUpdate: builder.mutation({
            query(body: IUpdateAlarmStatus) {
                return {
                    url: `Alarms/Status/Update`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => [{ type: 'Alarms', id: arg.alarmID }, { type: 'AlarmStatus', id: arg.alarmID }]
        }),
        AlarmFilters: builder.query<IAlarmFilterListDTO[], string>({
            query: () => "AlarmFilters/UserList",
            providesTags: (result, error, arg) => ['AlarmFilters']
        }),
        UpdateAlarmFilters: builder.mutation({
            query(body: IAlarmFilterDTO[]) {
                return {
                    url: `AlarmFilters/UpdateFilters`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => ["AlarmFilters"]
        }),
        AlarmSettings: builder.query<IAlarmSetting[], string>({
            query: () => "Alarms/Settings",
            providesTags: ['AlarmSettings']
        }),
        AlarmSetting: builder.query<IAlarmSetting, number>({
            query: (AlarmSettingID) => `Alarms/Setting/${AlarmSettingID}`,
            providesTags: (result, error, arg) => [{ type: 'AlarmSettings', id: arg }]
        }),
        AddAlarmSetting: builder.mutation({
            query(body: INewAlarmSetting) {
                return {
                    url: `Alarms/Settings/Add`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => ["AlarmSettings"]
        }),
        UpdateAlarmSetting: builder.mutation({
            query(body: IUpdateAlarmSetting) {
                return {
                    url: `Alarms/Settings/Update`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => ['AlarmSettings', { type: 'AlarmSettings', id: arg.alarmSettingID }]
        }),
        DeleteAlarmSetting: builder.mutation({
            query: (alarmSettingID: number) => `Alarms/Settings/Delete/${alarmSettingID}`,
            invalidatesTags: (result, error, arg) => ['AlarmSettings', { type: 'AlarmSettings', id: arg }]
        }),
        UserAccount: builder.query<IUser, string>({
            query: () => "UserAccount",
            providesTags: ['User']
        }),
        UpdateUserAccount: builder.mutation({
            query(body: IUpdateUser) {
                return {
                    url: `UserAccount/Update`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => ['User']
        }),
        UserPermissions: builder.query<ISecurityRoles, string>({
            query: () => "UserAccount/SecurityRoles",
            providesTags: ['SecurityRoles']
        }),
        UserSettings: builder.query<IUserSetting[], string>({
            query: () => "UserAccount/Settings",
            providesTags: ['UserSettings']
        }),
        SetUserSetting: builder.mutation({
            query(body: IUpdateUserSetting) {
                return {
                    url: `UserAccount/UpdateSetting`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: ['User']
        }),
        Organisations: builder.query<IOrganisationTreeNode[], string>({
            query: () => "Organisations",
        }),
        UserAccountSetCurrentOrganisation: builder.mutation({
            query: user => `Organisations/SetCurrent/${user.currentOrganisationID}`,
            invalidatesTags: allTagTypes
        }),
        MeterCategories: builder.query<IMeterTreeNodeCategory[], string>({
            query: () => "MeterCategories",
        }),
        MeterTreeNodeCategories: builder.query<IMeterTreeNodeCategory[], string>({
            query: (NodeID) => `MeterTree/NodeCategories/${NodeID}`,
            providesTags: (result, error, arg) => [{ type: 'MeterTreeNodeCategory', id: arg }]
        }),
        MeterTreeNodeDeBs: builder.query<IMeterTreeNodeDeBs, string>({
            query: (nodeID) => `DeBs/NodeDeBs/${nodeID}`,
            providesTags: (result, error, arg) => [{ type: 'MeterTreeNodeDeBs', id: arg }]
        }),
        AddMeterTreeNodeCategories: builder.mutation({
            query: ({ NodeID, MeterTreeCategoryID }) => `MeterTree/NodeCategories/Add/${NodeID}/${MeterTreeCategoryID}`,
            invalidatesTags: (result, error, arg) => [{ type: 'MeterTreeNodeCategory', id: arg.NodeID }]
        }),
        DeleteMeterTreeNodeCategories: builder.mutation({
            query: ({ NodeID, MeterTreeCategoryID }) => `MeterTree/NodeCategories/Delete/${NodeID}/${MeterTreeCategoryID}`,
            invalidatesTags: (result, error, arg) => [{ type: 'MeterTreeNodeCategory', id: arg.NodeID }]
        }),
        DeleteMeterGroup: builder.mutation({
            query: (nodeID) => `MeterGroup/Delete/${nodeID}`,
            invalidatesTags: (result, error, arg) => ["MeterTree", { type: 'MeterGroup', id: arg.nodeID }]
        }),
        AddMeterGroupMeter: builder.mutation({
            query: ({ nodeID, meterID, isSubtraction }) => `MeterGroup/AddMeter/${nodeID}/${meterID}/${isSubtraction}`,
            invalidatesTags: (result, error, arg) => [{ type: 'MeterGroup', id: arg.nodeID }]
        }),
        DeleteMeterGroupMeter: builder.mutation({
            query: ({ nodeID, meterID }) => `MeterGroup/DeleteMeter/${nodeID}/${meterID}`,
            invalidatesTags: (result, error, arg) => [{ type: 'MeterGroup', id: arg.nodeID }]
        }),
        UpdateNodeDeBs: builder.mutation({
            query(body: IMeterTreeNodeDeBs) {
                return {
                    url: `DeBs/NodeDeBs/Update`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => [{ type: 'MeterTreeNodeDeBs', id: arg.nodeID }]
        }),
        MeterExpectedData: builder.query<IExpectedData[], string>({
            query: (MeterID) => `Meter/ExpectedData/${MeterID}`,
            providesTags: (result, error, arg) => [{ type: 'MeterExpectedData', id: arg }]
        }),
        AddMeterExpectedData: builder.mutation({
            query(body: IExpectedData) {
                return {
                    url: `Meter/AddExpectedData`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => [{ type: 'MeterExpectedData', id: arg.meterID }]
        }),
        DeleteMeterExpectedData: builder.mutation({
            query(body: IExpectedData) {
                return {
                    url: `Meter/DeleteExpectedData`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => [{ type: 'MeterExpectedData', id: arg.meterID }]
        }),
        MeterOrganisations: builder.query<IMeterOrganisation[], number>({
            query: MeterID => `Meter/${MeterID}/Organisations`,
            providesTags: (result, error, arg) => [{ type: 'MeterOrganisations', id: arg }]
        }),
        DataTypes: builder.query<IDataType[], string>({
            query: () => "DataTypes"
        }),
        DataUnits: builder.query<IDataUnit[], string>({
            query: () => "DataUnits"
        }),
        Hardware: builder.query<IHardware[], number>({
            query: (MeterID) => {
                return MeterID ? `Hardware/${MeterID}` : 'Hardware';
            },
        }),
        MeterHardware: builder.query<IMeterHardware[], string>({
            query: (MeterID) => `Meter/Hardware/${MeterID}`,
            providesTags: (result, error, arg) => [{ type: 'MeterHardware', id: arg }]
        }),
        AddMeterHardware: builder.mutation({
            query: (meterHardware: IMeterHardwareQuery) => `Meter/AddHardware/${meterHardware.meterID}/${meterHardware.hardwareGUID}`,
            invalidatesTags: (result, error, arg) => [{ type: 'MeterHardware', id: arg.meterID }]
        }),
        DeleteMeterHardware: builder.mutation({
            query: (meterHardware: IMeterHardware) => `Meter/DeleteHardware/${meterHardware.meterID}/${meterHardware.hardwareGUID}`,
            invalidatesTags: (result, error, arg) => [{ type: 'MeterHardware', id: arg.meterID }]
        }),
        UpdateMeterHardware: builder.mutation({
            query(body: IMeterHardware) {
                return {
                    url: `Meter/UpdateHardware`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => [{ type: 'MeterHardware', id: arg.meterID }]
        }),
        MoveMeterHardware: builder.mutation({
            query(body: IMoveMeterHardware) {
                return {
                    url: `Meter/MoveHardware`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: 'MeterHardware', id: arg.meterID },
                { type: 'MeterHardware', id: arg.newMeterID },
                { type: 'MeterDataChannels', id: arg.meterID },
                { type: 'MeterDataChannels', id: arg.newMeterID }
            ]
        }),
        MeterDataChannels: builder.query<IMeterDataChannel[], number>({
            query: (MeterID) => `Meter/DataChannels/${MeterID}`,
            transformResponse: (response: IMeterDataChannel[], meta, arg) => response.map((item) => { return { ...item, multiplierCount: item.multipliers.length } }),
            providesTags: (result, error, arg) => [{ type: 'MeterDataChannels', id: arg }]
        }),
        RebuildAllData: builder.mutation({
            query: (meterID: number) => `Meter/DataChannels/RebuildAllData/${meterID}`
        }),
        AddDataChannelMultiplier: builder.mutation({
            query(body: IAddMeterDataChannelMultiplier) {
                return {
                    url: `Meter/AddDataChannelMultiplier`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => [{ type: 'MeterDataChannels', id: arg.meterID }]
        }),
        UpdateDataChannelMultiplier: builder.mutation({
            query(body: IMeterDataChannelMultiplier) {
                return {
                    url: `Meter/UpdateDataChannelMultiplier`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => [{ type: 'MeterDataChannels', id: arg.meterID }]
        }),
        UpdateMeterDataChannel: builder.mutation({
            query(body: IMeterDataChannelUpdate) {
                return {
                    url: `Meter/DataChannels/Update`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => [{ type: 'MeterDataChannels', id: arg.meterID }]
        }),
        CopyOrMoveDataChannel: builder.mutation({
            query(body: IDataChannelCopyDTO) {
                return {
                    url: `Meter/DataChannels/CopyOrMove`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => [{ type: 'MeterDataChannels', id: arg.MeterID }, { type: 'MeterDataChannels', id: arg.NewMeterID }]
        }),
        DeleteMeterDataChannel: builder.mutation({
            query: (body: IMeterDataChannelUpdate) => `Meter/DataChannels/Delete/${body.meterDataChannelGUID}`,
            invalidatesTags: (result, error, arg) => [{ type: 'MeterDataChannels', id: arg.meterID }]
        }),
        CloneDataChannel: builder.mutation({
            query(body: IDataChannelCloneDTO) {
                return {
                    url: `Meter/DataChannels/Clone`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => [{ type: 'MeterDataChannels', id: arg.MeterID }, { type: 'MeterDataChannels', id: arg.MeterID }]
        }),
        DeleteDataChannelMultiplier: builder.mutation({
            query: (meterDataChannelMultiplier: IMeterDataChannelMultiplier) => `Meter/DeleteDataChannelMultiplier/${meterDataChannelMultiplier.meterDataChannelMultiplierID}`,
            invalidatesTags: (result, error, arg) => [{ type: 'MeterDataChannels', id: arg.meterID }]
        }),
        Dashboard_Volume: builder.query<any, IDashboardQueryDTO>({
            query: (body: IDashboardQueryDTO) => {
                return {
                    url: 'Dashboard/Volume',
                    method: 'POST',
                    body,
                }
            },
            providesTags: (result, error, arg) => ['Dashboard']
        }),
        Dashboard_Alarms: builder.query<any, IDashboardQueryDTO>({
            query: (body: IDashboardQueryDTO) => {
                return {
                    url: 'Dashboard/Alarms',
                    method: 'POST',
                    body,
                }
            },
            providesTags: (result, error, arg) => ['Dashboard']
        }),
        Dashboard_DataReceived: builder.query<any, IDashboardQueryDTO>({
            query: (body: IDashboardQueryDTO) => {
                return {
                    url: 'Dashboard/DataReceived',
                    method: 'POST',
                    body,
                }
            },
            providesTags: (result, error, arg) => ['Dashboard']
        }),
        ChartTabs: builder.query<IChartTab[], string>({
            query: () => "Chart/ChartTabs",
            providesTags: (result) =>
                result
                    ? [
                        ...result.map(({ tabGUID }) => ({ type: 'ChartTabs' as const, tabGUID })),
                        { type: 'ChartTabs', id: 'LIST' },
                    ]
                    : [{ type: 'ChartTabs', id: 'LIST' }],
        }),
        ChartTab: builder.query<IChartTab, string>({
            query: (tabGUID: string) => `Chart/ChartTab/${tabGUID}`,
            providesTags: (result, error, arg) => [{ type: 'ChartTabs', id: arg }]
        }),
        ChartData: builder.query<any, IChartDataQuery>({
            query: (arg) => {
                return {
                    url: 'Chart/Data',
                    params: { ...arg },
                };
            },
            //providesTags: (result, error, arg) => [{ type: 'ChartData', id: arg.NodeID }]
        }),
        UpdateChartTab: builder.mutation({
            query(body: IChartTabUpdate) {
                return {
                    url: `Chart/ChartTabs/Update`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => [{ type: 'ChartTabs', id: arg.tabGUID }]
        }),
        AddChartTabNode: builder.mutation({
            query(body: IChartTabAddNode) {
                return {
                    url: `Chart/ChartTabs/Add`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: ['ChartTabs']
        }),
        DeleteChartTab: builder.mutation({
            query: ({ tabGUID }) => `Chart/ChartTabs/Delete/${tabGUID}`,
            invalidatesTags: ['ChartTabs']
        }),
        ChartTabsCLoseAll: builder.mutation({
            query: () => `Chart/ChartTabs/DeleteAll`,
            invalidatesTags: ['ChartTabs']
        }),
        SetChartColour: builder.mutation({
            query(body: { NodeID: number, DataTypeID: number, Colour: string }) {
                return {
                    url: `Chart/SetChartColour`,
                    method: 'POST',
                    body,
                }
            }
        }),
        APIKeys: builder.query<IAPIKey[], string>({
            query: () => "APIKeys"
        }),
        MissingData: builder.query<IMissingData[], { nodeID: number, startDate: string, endDate: string }>({
            query: ({ nodeID, startDate, endDate }) => `MissingData?ParentNodeID=${nodeID}&StartDate=${startDate}&EndDate=${endDate}`,
            providesTags: (result, error, arg) => ['MissingData']
        }),
        AddDataRequests: builder.mutation({
            query(body: IDataRequest[]) {
                return {
                    url: `MissingData/AddRequests`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: ['MissingData']
        }),
        DataRequestHistory: builder.query<IDataRequest[], IDataRequestHistoryQuery>({
            query: ({ meterID, startDate, endDate }) => `MissingData/RequestHistory?MeterID=${meterID}&StartDate=${startDate}&EndDate=${endDate}`,
            providesTags: (result, error, arg) => [{ type: 'DataRequestHistory', id: arg.meterID }]
        }),
        DataRequestHistoryStatus: builder.query<IDataRequest[], IDataRequestHistoryStatusQuery>({
            query: ({ startDate, endDate }) => `MissingData/RequestHistoryStatus?StartDate=${startDate}&EndDate=${endDate}`,
        }),
        AutomaticRequests: builder.query<IAutomaticRequest[], string>({
            query: () => `MissingData/AutomaticRequests`,
            providesTags: (result, error, arg) => ['AutomaticRequests']
        }),
        UpdateAutomaticRequest: builder.mutation({
            query(body: IUpdateAutomaticRequest) {
                return {
                    url: `MissingData/UpdateAutomaticRequest`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: ['AutomaticRequests']
        }),
        DataCorrections: builder.query<IDataCorrection[], IDataCorrectionQuery>({
            query: ({ startDate, endDate, parentNodeID }) => { return `DataCorrections?StartDate=${startDate}&EndDate=${endDate}` + (parentNodeID ? '&parentNodeID=' + parentNodeID : '') },
            providesTags: (result, error, arg) => ['DataCorrections']
        }),
        AddDataCorrection: builder.mutation({
            query(body: IAddDataCorrection) {
                return {
                    url: `DataCorrections/AddCorrection`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: ['DataCorrections']
        }),
        MeterTreeNodeCollections: builder.query<IMeterTreeNodeCollection[], string>({
            query: () => { return `MeterTreeNodeCollections/List` },
            providesTags: (result, error, arg) => ['MeterTreeNodeCollections']
        }),
        MeterTreeNodeCollection: builder.query<IMeterTreeNodeCollection, string>({
            query: (meterTreeNodeCollectionGUID) => { return `MeterTreeNodeCollections/${meterTreeNodeCollectionGUID}` },
            providesTags: (result, error, arg) => [{ type: 'MeterTreeNodeCollection', id: arg }]
        }),
        AddMeterTreeNodeCollectionNode: builder.mutation<IMeterTreeNodeCollection, INewMeterTreeNodeCollectionNode>({
            query(body: INewMeterTreeNodeCollectionNode) {
                return {
                    url: `MeterTreeNodeCollections/AddNode`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => [{ type: 'MeterTreeNodeCollection', id: arg.meterTreeNodeCollectionGUID }]
        }),
        DeleteMeterTreeNodeCollectionNode: builder.mutation<IMeterTreeNodeCollection, IDeleteMeterTreeNodeCollectionNode>({
            query(body: IDeleteMeterTreeNodeCollectionNode) {
                return {
                    url: `MeterTreeNodeCollections/DeleteNode`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => [{ type: 'MeterTreeNodeCollection', id: arg.meterTreeNodeCollectionGUID }]
        }),

        DeBsReports: builder.query<IDeBsReport[], IDeBsReportQuery>({
            query: ({ startDate, endDate }) => `DeBs/Reports?StartDate=${startDate}&EndDate=${endDate}`,
            providesTags: (result, error, arg) => ['DeBsReports']
        }),
        DeBsRowCount: builder.query<{ potentialRowCount: number, currentRowCount: number }, IDeBsReportQuery>({
            query: ({ startDate, endDate }) => `DeBs/Reports/RowCount?StartDate=${startDate}&EndDate=${endDate}`,
            keepUnusedDataFor: 10,
        }),
        DeBsMeters: builder.query<IMeterTreeNode[], string>({
            query: () => "DeBs/Meters",
        }),
        DeBsAddReport: builder.mutation({
            query: ({ startDate, endDate }) => `DeBs/Reports/Add?StartDate=${startDate}&EndDate=${endDate}`,
            invalidatesTags: ['DeBsReports']
        }),
        DeBsDrakewellFiles: builder.query<IDeBsDrakewellFile[], IDeBsReportQuery>({
            query: ({ startDate }) => `DeBs/DrakewellFiles?StartDate=${startDate}`,
        }),

        SecurityTree: builder.query<ISecurityTree[], string>({
            query: () => `Security/Tree`,
            providesTags: (result, error, arg) => ['Security']
        }),
        SecurityUserDetails: builder.query<IUser, number>({
            query: (userID) => `Security/User/${userID}`,
            providesTags: (result, error, arg) => [{ type: 'UserDetails', id: arg }]
        }),
        AddUser: builder.mutation<IUser, INewUser>({
            query(body: INewUser) {
                return {
                    url: `Security/Users/Add`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => ["Security"]
        }),
        EditUser: builder.mutation<IUser, IUpdateUser>({
            query(body: IUpdateUser) {
                return {
                    url: `Security/Users/Edit`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => ['Security', 'SecurityRoles', 'UserDetails', { type: 'UserDetails', id: arg.userID }]
        }),
        DeleteUser: builder.mutation<null, IDeleteUser>({
            query(body: IDeleteUser) {
                return {
                    url: `Security/Users/Delete`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => ['Security', 'SecurityRoles', 'UserDetails', { type: 'UserDetails', id: arg.userID }]
        }),
        SecuritySecurityGroupDetails: builder.query<IUpdateSecurityGroup, number>({
            query: (userID) => `Security/SecurityGroup/${userID}`,
            providesTags: (result, error, arg) => [{ type: 'SecurityGroup', id: arg }]
        }),
        SecurityGroupPermissions: builder.query<ISecurityGroupPermissions, number>({
            query: (securityGroupID) => `Security/SecurityGroupPermissions/${securityGroupID}`,
            providesTags: (result, error, arg) => ['SecurityRoles', { type: 'SecurityGroupPermissions', id: arg }]
        }),
        AddSecurityGroup: builder.mutation<IUpdateSecurityGroup, INewSecurityGroup>({
            query(body: IUpdateSecurityGroup) {
                return {
                    url: `Security/SecurityGroup/Add`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => ['Security']
        }),
        EditSecurityGroup: builder.mutation<IUpdateSecurityGroup, IUpdateSecurityGroup>({
            query(body: IUpdateSecurityGroup) {
                return {
                    url: `Security/SecurityGroup/Edit`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => ['Security', 'SecurityRoles', 'UserDetails', { type: 'SecurityGroup', id: arg.userGroupID }, { type: 'SecurityGroupPermissions', id: arg.userGroupID }]
        }),
        DeleteSecurityGroup: builder.mutation<null, IDeleteSecurityGroup>({
            query(body: IDeleteSecurityGroup) {
                return {
                    url: `Security/SecurityGroup/Delete`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => ['Security', 'SecurityRoles', 'UserDetails', { type: 'SecurityGroup', id: arg.userGroupID }, { type: 'SecurityGroupPermissions', id: arg.userGroupID }]
        }),
        OrganisationSecurity: builder.query<IOrganisationSecurity, number>({
            query: (organisationID) => `Security/OrganisationSecurity/${organisationID}`,
            providesTags: (result, error, arg) => [{ type: 'OrganisationSecurity', id: arg }]
        }),
        OrganisationDetails: builder.query<IOrganisationTreeNode, number>({
            query: (organisationID) => `Security/OrganisationDetails/${organisationID}`,
            providesTags: (result, error, arg) => [{ type: 'Organisation', id: arg }]
        }),
        AddOrganisation: builder.mutation<IOrganisationTreeNode, INewOrganisation>({
            query(body: INewOrganisation) {
                return {
                    url: `Security/Organisations/Add`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => ['Security']
        }),
        EditOrganisation: builder.mutation<IOrganisationTreeNode, IEditOrganisation>({
            query(body: IEditOrganisation) {
                return {
                    url: `Security/Organisations/Edit`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => ['Security', 'SecurityRoles', { type: 'Organisation', id: arg.organisationID }, { type: 'OrganisationSecurity', id: arg.organisationID }]
        }),

        Report15Minutes: builder.mutation({
            queryFn: async ({ startDate, endDate, fileName }, api, extraOptions, baseQuery) => {
                const result = await baseQuery({
                    url: `DeBs/Report15Minutes?StartDate=${startDate}&EndDate=${endDate}`,
                    responseHandler: ((response) => response.blob())
                })
                var hiddenElement = document.createElement('a');
                var url = window.URL || window.webkitURL;
                var fileURL = url.createObjectURL(result.data as any);
                hiddenElement.href = fileURL;
                hiddenElement.target = '_blank';
                hiddenElement.download = fileName;
                hiddenElement.click();
                return { data: null }
            }
        }),
        Licenses: builder.query<ILicense[], null>({
            query: () => `License/List`,
            providesTags: ['License']
        }),
        License: builder.query<ILicense, string>({
            query: (licenseGuid) => `License/Item/${licenseGuid}`,
            providesTags: (result, error, arg) => [{ type: 'License', id: arg }]
        }),
        LicenseHardware: builder.query<ILicensedHardware[], string>({
            query: (licenseGuid) => `License/Hardware/${licenseGuid}`,
            providesTags: (result, error, arg) => [{ type: 'LicenseHardware', id: arg }]
        }),
        UpdateLicenseAutoRenewal: builder.mutation<null, IUpdateLicenseAutoRenewal>({
            query(body: IUpdateLicenseAutoRenewal) {
                return {
                    url: `License/UpdateAutoRenewal`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => [{ type: 'LicenseHardware', id: arg.licenseGUID }]
        }),
        AddHardwareLicense: builder.mutation<null, IAddHardwareLicense>({
            query(body: IAddHardwareLicense) {
                return {
                    url: `License/AddHardwareLicense`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => ['MeterHardware', { type: 'LicenseHardware', id: arg.licenseGUID }]
        }),
        CreateLicense: builder.mutation<null, ICreateLicense>({
            query(body: ICreateLicense) {
                return {
                    url: `License/CreateLicense`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => ['License']
        }),
        DataAPIs: builder.query<IDataAPIs[], string>({
            query: () => `data/apis`
        }),
       
    }),

})




// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useMeterTreeQuery,
    useUserAccountQuery,
    useUpdateUserAccountMutation,
    useOrganisationsQuery,
    useUserAccountSetCurrentOrganisationMutation,
    useMeterTree_MeterQuery,
    useMeterTree_AddMeterMutation,
    useMeterTree_UpdateMeterMutation,
    useMeterTree_GroupQuery,
    useMeterTree_AddGroupMutation,
    useMeterTree_UpdateGroupMutation,
    useMeterTree_SiteQuery,
    useMeterTree_AddSiteMutation,
    useMeterTree_UpdateSiteMutation,
    useDataSourcesQuery,
    useMeterCategoriesQuery,
    useMeterTreeNodeCategoriesQuery,
    useAddMeterTreeNodeCategoriesMutation,
    useDeleteMeterTreeNodeCategoriesMutation,
    useAddMeterGroupMeterMutation,
    useDeleteMeterGroupMeterMutation,
    useMeterTreeNodeDeBsQuery,
    useUpdateNodeDeBsMutation,
    useMeterExpectedDataQuery,
    useAddMeterExpectedDataMutation,
    useDeleteMeterExpectedDataMutation,
    useDataTypesQuery,
    useMeterHardwareQuery,
    useDeleteMeterHardwareMutation,
    useHardwareQuery,
    useAddMeterHardwareMutation,
    useUpdateMeterHardwareMutation,
    useMeterDataChannelsQuery,
    useAddDataChannelMultiplierMutation,
    useUpdateDataChannelMultiplierMutation,
    useDeleteDataChannelMultiplierMutation,
    useDashboard_VolumeQuery,
    useDashboard_AlarmsQuery,
    useDashboard_DataReceivedQuery,
    useRebuildAllDataMutation,
    useUpdateMeterDataChannelMutation,
    useCopyOrMoveDataChannelMutation,
    useDeleteMeterDataChannelMutation,
    useCloneDataChannelMutation,
    useAlarmsQuery,
    useDataAlarmsQuery,
    useAlarmStatusHistoryQuery,
    useAlarmStatusUpdateMutation,
    useChartTabsQuery,
    useChartTabQuery,
    useChartDataQuery,
    useUpdateChartTabMutation,
    useDataUnitsQuery,
    useAddChartTabNodeMutation,
    useDeleteChartTabMutation,
    useChartTabsCLoseAllMutation,
    useAlarmFiltersQuery,
    useUpdateAlarmFiltersMutation,
    useAlarmQuery,
    useSetChartColourMutation,
    useAPIKeysQuery,
    useDataSourceQuery,
    useDataSourceInterpretedContentQuery,
    useDataSourceDataChannelDataQuery,
    useDataSourceMeterDataQuery,
    useDataSourceUpdateHardwareMutation,
    useDataSourceReprocessMutation,
    useDataSourceInvalidateMutation,
    useDataSourceTypesQuery,
    useDataSourceUpdateDataSourceTypeMutation,
    useMissingDataQuery,
    useAddDataRequestsMutation,
    useDataRequestHistoryQuery,
    useAutomaticRequestsQuery,
    useUpdateAutomaticRequestMutation,
    useDataRequestHistoryStatusQuery,
    useDataCorrectionsQuery,
    useAddDataCorrectionMutation,
    useMeterOrganisationsQuery,
    useUserPermissionsQuery,
    useAlarmSettingsQuery,
    useAddAlarmSettingMutation,
    useMeterTreeNodeCollectionsQuery,
    useMeterTreeNodeCollectionQuery,
    useAddMeterTreeNodeCollectionNodeMutation,
    useDeleteMeterTreeNodeCollectionNodeMutation,
    useDeleteMeterGroupMutation,
    useUpdateAlarmSettingMutation,
    useDeleteAlarmSettingMutation,
    useAlarmSettingQuery,
    useDeleteMeterSiteMutation,
    useDeBsReportsQuery,
    useDeBsMetersQuery,
    useDeBsAddReportMutation,
    useDeBsRowCountQuery,
    useDeBsDrakewellFilesQuery,
    useMeterGrantAccessMutation,
    useMeterRevokeAccessMutation,
    useMeterTree_UpdateMeterSecurityMutation,
    useMeterTree_UpdateMeterControllingOrganisationMutation,
    useDeleteMeterMutation,
    useSecurityTreeQuery,
    useAddUserMutation,
    useEditUserMutation,
    useDeleteUserMutation,
    useSecurityUserDetailsQuery,
    useSecuritySecurityGroupDetailsQuery,
    useEditSecurityGroupMutation,
    useAddSecurityGroupMutation,
    useOrganisationSecurityQuery,
    useSecurityGroupPermissionsQuery,
    useOrganisationDetailsQuery,
    useAddOrganisationMutation,
    useEditOrganisationMutation,
    useDeleteSecurityGroupMutation,
    useUserSettingsQuery,
    useSetUserSettingMutation,
    useReport15MinutesMutation,
    useMoveMeterHardwareMutation,
    useLicensesQuery,
    useLicenseQuery,
    useLicenseHardwareQuery,
    useUpdateLicenseAutoRenewalMutation,
    useAddHardwareLicenseMutation,
    useCreateLicenseMutation,
    useDataAPIsQuery,
} = metervueApi